import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { AppleIcon } from '@/components/decorative/apple-icon'
import { GoogleIcon } from '@/components/decorative/google-icon'
import { Button } from '@/components/ui/button'
import { Field, FieldSeparator } from '@/components/ui/field'
import { authClient } from '@/lib/auth-client'
import { type AuthFlow, flowDestination, flowHeaders, upgradeFailureReason } from '@/lib/auth-flow'
import { toast } from '@/lib/toast'

// Display order. Apple asks that its button be at least as prominent as the others.
const PROVIDERS = {
  apple: { label: 'Continue with Apple', Icon: AppleIcon },
  google: { label: 'Continue with Google', Icon: GoogleIcon },
} as const

type Provider = keyof typeof PROVIDERS

// Guest upgrades carry their source through server-controlled OAuth state.
const SOCIAL_FLOWS: ReadonlySet<AuthFlow['kind']> = new Set(['account', 'handoff', 'guestUpgrade'])

/** The providers the backend has credentials for. Load it in the route loader. */
export const socialProvidersQueryOptions = () =>
  queryOptions({
    queryKey: ['social-providers'],
    queryFn: async (): Promise<Provider[]> => {
      // An unreachable or older backend leaves email sign-in, rather than an error page.
      const { data } = await authClient.socialProviders().catch(() => ({ data: null }))
      return (Object.keys(PROVIDERS) as Provider[]).filter((p) => data?.providers.includes(p))
    },
    staleTime: Number.POSITIVE_INFINITY,
  })

/** Better Auth appends `error` to errorCallbackURL when a Google or Apple sign-in fails. */
export const socialSignInSearch = { error: z.string().optional() }

const ERROR_TOAST_ID = 'social-sign-in-error'

const ERRORS: Record<string, (resetPassword: () => void) => ReactNode> = {
  access_denied: () => 'Sign-in was cancelled.',
  // Better Auth links a provider only to accounts whose email is already verified. Resetting the
  // password verifies it and replaces any password someone else set, so the provider links next time.
  account_not_linked: (resetPassword) => (
    <>
      This email already has an account.{' '}
      <button
        type="button"
        className="cursor-pointer text-primary underline underline-offset-2"
        onClick={resetPassword}
      >
        Reset its password
      </button>{' '}
      to link Google or Apple.
    </>
  ),
}

/**
 * Shows a failed Google or Apple sign-in once, then drops `error` from the URL. `onResetPassword`
 * runs when the message's reset link is pressed.
 */
export function useSocialSignInError(error: string | undefined, onResetPassword: () => void) {
  const navigate = useNavigate()
  // The toast outlives this render, so its link reads the latest callback through a ref.
  const onResetPasswordRef = useRef(onResetPassword)
  onResetPasswordRef.current = onResetPassword
  useEffect(() => {
    if (!error) return
    toast({
      id: ERROR_TOAST_ID,
      description:
        ERRORS[error]?.(() => {
          toast.dismiss(ERROR_TOAST_ID)
          onResetPasswordRef.current()
        }) ?? 'Couldn’t sign in with that account. Try again, or use email.',
      errorCode: error,
    })
    navigate({ to: '.', search: (prev) => ({ ...prev, error: undefined }), replace: true })
  }, [error, navigate])
}

/** Apple and Google buttons above the email form; renders nothing when neither applies. */
export function SocialSignIn({ flow, disabled }: { flow: AuthFlow; disabled?: boolean }) {
  const { data: providers } = useSuspenseQuery(socialProvidersQueryOptions())
  const router = useRouter()
  const navigate = useNavigate()
  const [pending, setPending] = useState<Provider>()

  if (!SOCIAL_FLOWS.has(flow.kind) || providers.length === 0) return null

  async function signIn(provider: Provider) {
    setPending(provider)
    const here = new URL(window.location.href)
    here.searchParams.delete('error')
    try {
      // Better Auth sends the browser to the provider, then back to one of these absolute URLs.
      const { error } = await authClient.signIn.social({
        provider,
        callbackURL: new URL(router.buildLocation(flowDestination(flow)).href, here).href,
        errorCallbackURL: here.href,
        fetchOptions: { headers: flowHeaders(flow) },
      })
      if (!error) return // The page is navigating to the provider; stay busy until it unloads.
      const reason = upgradeFailureReason(flow, error.code)
      if (reason) {
        await navigate({ to: '/upgrade-failed', search: { reason } })
        return
      }
      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
    setPending(undefined)
  }

  return (
    <>
      <Field>
        {providers.map((provider) => {
          const { label, Icon } = PROVIDERS[provider]
          return (
            <Button
              key={provider}
              variant="outline"
              className="w-full [&_svg]:size-5"
              disabled={disabled || pending !== undefined}
              aria-busy={pending === provider}
              onPress={() => signIn(provider)}
            >
              <Icon />
              {label}
            </Button>
          )
        })}
      </Field>
      <FieldSeparator>Or continue with email</FieldSeparator>
    </>
  )
}
